/**
 * Note threading — pure grouping helpers for the unified `notes` collection.
 *
 * Kept out of `notes.service.ts` on purpose: that module imports `@/firebase`,
 * and these helpers are used by timeline entry builders that are unit-tested
 * as plain data functions. Pulling the Firebase SDK into those tests just to
 * group an array would be a needless dependency.
 *
 * A reply is an ordinary note carrying `parentNoteId`. It lives in the same
 * entity as its parent, so every existing subscription already returns it —
 * these helpers are what stop it rendering as a loose top-level note.
 */

import type { Note } from '@/types';

/** A root note plus the replies hanging off it, oldest reply first. */
export interface NoteThread {
  note: Note;
  replies: Note[];
}

/**
 * Split a flat note list into threads.
 *
 * Roots keep the incoming order (already pinned-first, newest-first from
 * `sortNotes`); replies are ordered oldest-first inside their thread, which is
 * how a conversation reads.
 *
 * A reply whose parent isn't in the list — parent soft-deleted, or a rolled-up
 * view that only loaded one of two entities — is promoted to a root rather
 * than dropped. Losing a message because its parent went missing is far worse
 * than showing it slightly out of place.
 */
export function buildNoteThreads(notes: Note[]): NoteThread[] {
  const byId = new Map<string, Note>();
  for (const n of notes) if (n.id) byId.set(n.id, n);

  const repliesByParent = new Map<string, Note[]>();
  const roots: Note[] = [];

  for (const n of notes) {
    const parentId = n.parentNoteId;
    if (parentId && byId.has(parentId)) {
      const list = repliesByParent.get(parentId);
      if (list) list.push(n);
      else repliesByParent.set(parentId, [n]);
    } else {
      roots.push(n);
    }
  }

  return roots.map((note) => ({
    note,
    replies: (note.id ? repliesByParent.get(note.id) ?? [] : []).sort((a, b) =>
      (a.createdAt || '').localeCompare(b.createdAt || ''),
    ),
  }));
}

/**
 * Everyone who has spoken in a thread — the root author plus every replier.
 * Feeds `createNoteReply`'s participant fanout, which is what makes a reply
 * reach the people already in the conversation without an explicit @tag.
 */
export function threadParticipants(
  root: Note,
  replies: Note[],
): { userId: string; userName: string }[] {
  const seen = new Set<string>();
  const out: { userId: string; userName: string }[] = [];

  for (const n of [root, ...replies]) {
    if (!n.createdBy || seen.has(n.createdBy)) continue;
    seen.add(n.createdBy);
    out.push({ userId: n.createdBy, userName: n.createdByName || 'Unknown' });
  }

  return out;
}

/**
 * Drop replies from a flat note list — for surfaces that show a COUNT or a
 * single preview line and would otherwise let a reply masquerade as a
 * top-level note (queue cards, job briefs, "latest note" strips).
 */
export function rootNotesOnly(notes: Note[]): Note[] {
  return notes.filter((n) => !n.parentNoteId);
}
